import { apiService } from '../../../shared/services/apiServices';
import { AxiosError } from 'axios';
import type { MediaResource } from '../types/addClientType';
import { CLIENT_ERROR_MESSAGES } from '../errors/errorMessages';
import clientEndpoints from './clientEndpoints';

export interface EditClientResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

export interface ClientPatchPayload {
  name?: string;
  email?: string;
  phoneNumber?: string;
  countryCode?: string;
  nip?: string;
  currency?: string;
  paymentStatus?: string;
  subscriptionPlanId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  profile?: MediaResource | null;
  docs?: MediaResource[];
  // Invoice details (Dane do faktury)
  billing_company_name?: string;
  billing_nip?: string;
  billing_regon?: string;
  billing_krs?: string;
  invoice_address_line_1?: string;
  invoice_address_line_2?: string;
  invoice_zip_code?: string;
  invoice_city?: string;
  invoice_province?: string;
}

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 403) {
    return CLIENT_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP;
  }
  if (!errorData) {
    return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }

  if (typeof errorData === 'string') {
    return errorData;
  }

  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    const msg = apiError.message || apiError.error;
    if (msg === 'Internal Server Error') {
      return CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR;
    }
    if (msg === 'Client with this email already exists') {
      return CLIENT_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS;
    }
    return msg || CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
  }

  return CLIENT_ERROR_MESSAGES.DEFAULT_ERROR;
};

export const editClient = async (
  clientId: string,
  payload: ClientPatchPayload
): Promise<EditClientResult> => {
  try {
    const result = await apiService.patchAPI(
      clientEndpoints.EDIT_DRAFT_CLIENT(clientId),
      payload,
      { skipGlobalErrorRedirect: true }
    );

    if (result.error) {
      return {
        success: false,
        error: result.isNetworkError
          ? CLIENT_ERROR_MESSAGES.NETWORK_ERROR
          : extractErrorMessage(result.data, result.status),
      };
    }

    return { success: true, data: result.data };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response && !!error.request) {
        return { success: false, error: CLIENT_ERROR_MESSAGES.NETWORK_ERROR };
      }
      return {
        success: false,
        error: extractErrorMessage(error.response?.data),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CLIENT_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
